<script setup lang="ts">
import dayjs from 'dayjs';
import { computed } from 'vue';
import { CompromissoForm } from '~/lib/fw';
import { useAuthenticationStore } from '~/stores/authenticationStore';
import { useDrawerStore } from '~/stores/drawer';
import { desabilitarDatasPraTras } from '~/utils';

interface Props {
  pastaCodigos?: string[];
  compromisso?: any;
  fluxo?: any;
}

const props = defineProps<Props>();

const emit = defineEmits(['close']);

const authenticationStore = useAuthenticationStore();
const drawerStore = useDrawerStore();

const btnSalvarDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});

const uiSolicitarAtualizacaoGcpj = () => {
  drawerStore.push2({
    componentName: 'CompromissoForm2',
    params: {
      componentName: 'SolicitarAtualizacaoGcpjForm',
      componentProps: {
        encaminharParaEquipeGcpj: true,
        pastaCodigos: [props.compromisso.fluxo.pastaCodigo],
      },
      title: 'SOLICITAR ATUALIZAÇÃO GCPJ',
    },
  });
};

const uiSolicitarPagamentoCustas = () => {
  drawerStore.push2({
    componentName: 'CompromissoForm3',
    params: {
      componentName: 'SolicitarPagamentoCustasForm',
      componentProps: {
        pastaCodigos: [props.fluxo.pasta.codigo],
      },
      title: 'SOLICITAR PAGAMENTO DE CUSTAS',
    },
  });
};

const uiSolicitarPesquisaCdi = () => {
  drawerStore.push2({
    componentName: 'CompromissoForm3',
    componentProps: {
      pastaCodigos: [props.fluxo.pasta.codigo],
    },
    title: 'SOLICITAR PESQUISA AO CDI',
  });
};

const uiSolicitarEncerramento = () => {
  drawerStore.push2({
    componentName: 'CompromissoForm2',
    params: {
      codigoPasta: props.fluxo.pasta.codigo,
      componentName: 'SolicitarEncerramentoForm',
      title: 'SOLICITAR ENCERRAMENTO',
    },
  });
};
</script>

<template>
  <CompromissoForm
    action="compromisso-pub/cumprir-compromisso-pub"
    :pasta-codigos="props.pastaCodigos ?? []"
    :compromisso-codigos="props.compromisso ? [props.compromisso.codigo] : []"
    :wait-for-result="true"
    :show-upload-files="true"
    :submit-button-disabled="btnSalvarDisabled"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-form-item
        label="Escolha uma das opções abaixo"
        name="seraCumprido"
        :rules="[
          {
            message: 'É necessário informar se o compromisso será cumprido',
            required: true,
            type: 'boolean',
          },
        ]"
      >
        <a-radio-group v-model:value="model.seraCumprido">
          <a-radio-button :value="true"> Cumprir agora </a-radio-button>
          <a-radio-button :value="false"> Reagendar </a-radio-button>
        </a-radio-group>
      </a-form-item>

      <a-form-item
        v-if="model.seraCumprido === true"
        label="Observação"
        name="observacao"
        :rules="[{ message: 'Observação é obrigatória', required: true }]"
      >
        <a-textarea v-model:value="model.observacao" :rows="4" />
      </a-form-item>

      <a-form-item
        v-if="model.seraCumprido === false"
        label="Novo vencimento"
        name="novoVencimento"
        :rules="[{ message: 'Novo vencimento é obrigatório', required: true }]"
      >
        <a-date-picker
          v-model:value="model.novoVencimento"
          format="DD/MM/YYYY HH:mm"
          value-format="DD/MM/YYYY HH:mm"
          placeholder="Escolha a data de vencimento"
          :disabled-date="desabilitarDatasPraTras"
          :show-time="{ defaultValue: dayjs('17:00:00', 'HH:mm:ss') }"
          style="width: 300px"
        />
      </a-form-item>

      <a-form-item
        v-if="model.seraCumprido === false"
        label="Justificativa"
        name="justificativaParaNaoCumprimento"
        :rules="[{ message: 'Justificativa é obrigatória', required: true }]"
      >
        <a-textarea
          v-model:value="model.justificativaParaNaoCumprimento"
          :rows="4"
        />
      </a-form-item>

      <a-form-item label="Atalhos">
        <a-space>
          <a-button @click="uiSolicitarAtualizacaoGcpj"
            >Atualizar GCPJ</a-button
          >
          <a-button @click="uiSolicitarPagamentoCustas">Pagar custas</a-button>
          <a-button @click="uiSolicitarPesquisaCdi">Pesquisa CDI</a-button>
          <a-button @click="uiSolicitarEncerramento">Encerramento</a-button>
        </a-space>
      </a-form-item>
    </template>
  </CompromissoForm>
</template>
